import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { UploadCloud, LayoutGrid, MessageCircle, Handshake, Check, Car } from "lucide-react";
import '../styles/how-it-works.css';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

const HowItWorksSection = () => {
  const sectionRef = useRef(null);
  const leftCardRef = useRef(null);
  const rightCardRef = useRef(null);
  const kineticGroupRef = useRef(null);
  const b1Ref = useRef(null);
  const b2Ref = useRef(null);
  const b3Ref = useRef(null);

  useEffect(() => {
    // 1. MODERN BLUR ENTRANCE
    const entrance = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "top 85%",
        toggleActions: "play none none none",
        once: true,
      }
    });

    entrance
      .fromTo(
        ".header-area",
        { opacity: 0, y: 20, filter: "blur(10px)" },
        { opacity: 1, y: 0, filter: "blur(0px)", duration: 1, immediateRender: false }
      )
      .fromTo(
        ".card-wrapper",
        { opacity: 0, scale: 0.9, filter: "blur(10px)" },
        { opacity: 1, scale: 1, filter: "blur(0px)", stagger: 0.2, duration: 1, immediateRender: false },
        "-=0.5"
      )
      .fromTo(
        kineticGroupRef.current,
        { opacity: 0, y: 50, filter: "blur(15px)" },
        { opacity: 1, y: 0, filter: "blur(0px)", duration: 1.5, ease: "power4.out", immediateRender: false },
        "-=1"
      )
      .fromTo(
        ".float-badge",
        { opacity: 0, scale: 0 },
        { opacity: 1, scale: 1, stagger: 0.1, duration: 0.8, ease: "back.out(1.7)", immediateRender: false },
        "-=0.8"
      );

    // 2. KINETIC FLOATING FOR CARDS
    gsap.to(leftCardRef.current, {
      y: -15,
      duration: 4,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });

    gsap.to(rightCardRef.current, {
      y: -15,
      duration: 4.5,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      delay: 0.5
    });

    // 3. KINETIC ORBITAL SWAY
    gsap.to(kineticGroupRef.current, {
      duration: 6,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
      motionPath: {
        path: [
          { x: 8, y: -8 },
          { x: -8, y: 8 },
          { x: 8, y: -8 }
        ],
        alignOrigin: [0.5, 0.5],
        autoRotate: false
      },
      rotationY: 10,
      rotationX: 5,
      rotationZ: 2,
      transformOrigin: "50% 50%"
    });

    // 4. SECONDARY INERTIA FOR BADGES
    gsap.to(b1Ref.current, { y: -8, duration: 2.5, repeat: -1, yoyo: true, ease: "sine.inOut" });
    gsap.to(b2Ref.current, { x: 5, duration: 3, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 0.5 });
    gsap.to(b3Ref.current, { y: -5, x: -3, duration: 4, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 1 });

    ScrollTrigger.refresh();
    const handleResize = () => ScrollTrigger.refresh();
    window.addEventListener('resize', handleResize);

    // 5. HIGH-VELOCITY MOUSE REACTION
    const handleMouseMove = (e) => {
      if (window.innerWidth < 1024) return;
      const xVal = (e.clientX / window.innerWidth - 0.5) * 18;
      const yVal = (e.clientY / window.innerHeight - 0.5) * -12;

      gsap.to(kineticGroupRef.current, {
        rotationY: xVal,
        rotationX: yVal,
        duration: 0.45,
        ease: "power2.out"
      });
    };

    document.addEventListener("mousemove", handleMouseMove);

    // Capture ref nodes so the cleanup uses the values from this effect run.
    const animatedNodes = [
      leftCardRef.current,
      rightCardRef.current,
      kineticGroupRef.current,
      b1Ref.current,
      b2Ref.current,
      b3Ref.current,
    ];

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener('resize', handleResize);
      gsap.killTweensOf(animatedNodes);
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <section className="section-container" id="how" ref={sectionRef}>
      <div className="container-wrapper">
        <div className="header-area">
          <span className="premium-badge">Seamless Experience</span>
          <h2>Up and Running in Minutes</h2>
        </div>

        <div className="main-row">
          {/* Left Card */}
          <div className="card-wrapper card-left" ref={leftCardRef}>
            <div className="glass-card">
              <div className="step-item">
                <div className="icon-box"><UploadCloud size={18} color="#FF8200" /></div>
                <h3>Download App</h3>
                <p>Secure login for iOS & Android.</p>
              </div>
              <div className="divider"></div>
              <div className="step-item">
                <div className="icon-box"><LayoutGrid size={18} color="#FF8200" /></div>
                <h3>List or Browse</h3>
                <p>Explore thousands of local ads.</p>
              </div>
            </div>
          </div>

          {/* Kinetic CenterPiece */}
          <div className="mockup-container" ref={kineticGroupRef}>
            <div className="float-badge badge-1" ref={b1Ref}>
              <div className="badge-icon bg-v"><Check size={12} /></div>
              <div className="badge-text"><b>Verified Seller</b><span>Lusaka</span></div>
            </div>

            <div className="float-badge badge-2" ref={b2Ref}>
              <div className="badge-icon bg-n"><Car size={12} /></div>
              <div className="badge-text"><b>New Match!</b><span>Toyota Hilux</span></div>
            </div>

            <div className="float-badge badge-3" ref={b3Ref}>
              <div className="badge-icon bg-c"><MessageCircle size={12} /></div>
              <div className="badge-text"><b>Chat Started</b><span>Secure</span></div>
            </div>

            <div className="phone-wrapper">
              <img src="/images/mockup.png" className="phone-img" alt="ZamWheelz" width="420" height="536" loading="lazy" decoding="async" />
            </div>
          </div>

          {/* Right Card */}
          <div className="card-wrapper card-right" ref={rightCardRef}>
            <div className="glass-card">
              <div className="step-item">
                <div className="icon-box"><MessageCircle size={18} color="#FF8200" /></div>
                <h3>Instant Chat</h3>
                <p>Real-time buyer/seller chat.</p>
              </div>
              <div className="divider"></div>
              <div className="step-item">
                <div className="icon-box"><Handshake size={18} color="#FF8200" /></div>
                <h3>Close the Deal</h3>
                <p>Finalize with peace of mind.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;